import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ExternalLink } from "lucide-react";
import { api } from "../../../lib/axios";
import TourneeHealthCard, { type TourneeHealthData } from "../../../components/tournees/TourneeHealthCard";
import { useTourneeLiveExecution } from "../../../modules/execution/hooks/useTourneeLiveExecution";
import { useExecutionMutations } from "../../../modules/execution/services/executionMutations";
import { frGpsStatus } from "../../../modules/execution/utils/qualityLabels";
import type { LivePassage } from "../../../modules/execution/types/execution.types";

type TourneeDetail = {
  id: number;
  numero: string | null;
  date: string;
  statut: string;
  executionStatus: string;
  commentaire?: string | null;
  zone?: { id: number; nom: string } | null;
  equipe?: { id: number; nom: string } | null;
  vehicule?: { id: number; immatriculation: string } | null;
  passages?: Array<{ id: number; executionStatus?: string }>;
};

type TimelineItem = {
  passageId: number;
  ordre: number | null;
  client: string;
  type: string;
  priorite: string;
  datePrevue: string;
  statut: string;
  executionStatus?: string;
  commentaire: string | null;
};

function labelTourneeExecution(ex?: string | null): string {
  switch (ex) {
    case "PLANIFIEE":
      return "Planifiée";
    case "EN_COURS":
      return "En cours";
    case "SUSPENDUE":
      return "Suspendue";
    case "TERMINEE":
      return "Terminée";
    default:
      return ex ?? "—";
  }
}

function labelPassageExecution(ex?: string | null): string {
  switch (ex) {
    case "PLANIFIE":
      return "Planifié";
    case "EN_COURS":
      return "En cours";
    case "REALISE":
      return "Réalisé";
    case "ECHEC":
      return "Échec";
    default:
      return ex ?? "—";
  }
}

function labelStatutPlanning(s?: string | null): string {
  switch (s) {
    case "PLANIFIE":
      return "Planifié";
    case "EN_COURS":
      return "En cours";
    case "REALISE":
      return "Réalisé";
    case "ANNULE":
      return "Annulé";
    default:
      return s ?? "—";
  }
}

export default function TourneeDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [tournee, setTournee] = useState<TourneeDetail | null>(null);
  const [health, setHealth] = useState<TourneeHealthData | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [workflowGov, setWorkflowGov] = useState<
    Array<{ id: number; action: string; label: string | null; createdAt: string; actorName: string | null }>
  >([]);

  const live = useTourneeLiveExecution(id);

  const loadDetailHealthTimeline = useCallback(async () => {
    if (!id) return;
    const [tRes, hRes, tlRes, wfRes] = await Promise.all([
      api.get(`/tournees/${id}`),
      api.get(`/tournees/${id}/health`),
      api.get(`/tournees/${id}/timeline`),
      api
        .get<{
          items?: Array<{ id: number; action: string; label: string | null; createdAt: string; actorName: string | null }>;
        }>(`/workflow/timeline/TOURNEE/${id}`)
        .catch(() => ({ data: { items: [] } })),
    ]);
    setTournee((tRes.data?.tournee ?? null) as TourneeDetail | null);
    setHealth((hRes.data?.health ?? null) as TourneeHealthData | null);
    setTimeline((tlRes.data?.items ?? []) as TimelineItem[]);
    setWorkflowGov(Array.isArray(wfRes.data?.items) ? wfRes.data.items : []);
  }, [id]);

  const refreshAll = useCallback(async () => {
    await loadDetailHealthTimeline();
    await live.refresh();
  }, [loadDetailHealthTimeline, live.refresh]);

  const mutations = useExecutionMutations({ tourneeId: id, refresh: refreshAll });

  useEffect(() => {
    void loadDetailHealthTimeline();
  }, [loadDetailHealthTimeline]);

  const execStatus = live.data?.tournee.executionStatus ?? tournee?.executionStatus ?? "PLANIFIEE";

  const hasRunningPassage = useMemo(() => {
    if (live.data?.tournee.passages?.length) {
      return live.data.tournee.passages.some((p) => p.executionStatus === "EN_COURS");
    }
    return tournee?.passages?.some((p) => p.executionStatus === "EN_COURS") ?? false;
  }, [live.data?.tournee.passages, tournee?.passages]);

  const closeGate = useMemo(() => {
    if (live.data?.canCloseTournee !== undefined) return live.data.canCloseTournee;
    return execStatus === "EN_COURS" && !hasRunningPassage;
  }, [live.data?.canCloseTournee, execStatus, hasRunningPassage]);

  const readOnly = execStatus === "TERMINEE";

  const dateLabel = useMemo(
    () => (tournee?.date ? new Date(tournee.date).toLocaleString("fr-FR") : "—"),
    [tournee?.date]
  );

  const showLaunchBanner = execStatus === "EN_COURS";

  const basePlanif = "/admin/planificateur/tournees";
  const execHref = id ? `/admin/execution/tournees/${id}` : basePlanif;

  const onCloseTournee = async () => {
    if (readOnly) return;
    if (hasRunningPassage) return;
    const warns = live.data?.closeWarnings ?? [];
    const warnBlock =
      warns.length > 0
        ? `\n\nAvertissements (non bloquants) :\n${warns.map((w) => `• ${w}`).join("\n")}`
        : "";
    if (!window.confirm(`Clôturer cette tournée ?${warnBlock}`)) return;
    await mutations.completeTournee();
  };

  return (
    <div className="space-y-5 p-4 text-slate-100 md:p-6">
      <header className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">Tournée</p>
            <h1 className="text-2xl font-bold text-white">{tournee?.numero ?? `#${tournee?.id ?? "—"}`}</h1>
          </div>
          <Link
            to={basePlanif}
            className="rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60"
          >
            ← Liste des tournées
          </Link>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-300 md:grid-cols-3">
          <p>Date : {dateLabel}</p>
          <p>Équipe : {tournee?.equipe?.nom ?? "—"}</p>
          <p>Mode opérationnel : {tournee?.vehicule ? "Véhicule" : "Sans véhicule"}</p>
          <p>Véhicule : {tournee?.vehicule?.immatriculation ?? "—"}</p>
          <p>Zone : {tournee?.zone?.nom ?? "—"}</p>
          <p>
            <span className="text-slate-500">Exécution :</span>{" "}
            <span className="font-semibold text-emerald-200">{labelTourneeExecution(execStatus)}</span>
          </p>
          <p className="md:col-span-2">
            <span className="text-slate-500">Planification (réf.) :</span> {tournee?.statut ?? "—"}
          </p>
        </div>
      </header>

      {showLaunchBanner ? (
        <div className="rounded-2xl border border-cyan-500/35 bg-cyan-950/25 px-4 py-3 text-sm text-cyan-50 ring-1 ring-cyan-400/20">
          <p className="font-semibold text-white">Tournée : {labelTourneeExecution("EN_COURS")}</p>
          <p className="mt-1 text-cyan-100/95">
            La tournée est lancée. Les passages de cette tournée passent en exécution terrain : statut « En cours »,
            action « Terminer » et photos avant/après sur la page exécution passages.
          </p>
        </div>
      ) : null}

      {health ? <TourneeHealthCard health={health} /> : null}

      <section className="rounded-2xl border border-slate-700 bg-slate-900/30 p-4 text-sm">
        <h2 className="text-lg font-semibold text-white">Timeline gouvernance</h2>
        <p className="mt-1 text-xs text-slate-500">
          Événements métier (`WorkflowEvent`). API : `GET /api/workflow/timeline/TOURNEE/:id`.
        </p>
        {workflowGov.length === 0 ? (
          <p className="mt-2 text-slate-500">Aucun événement ou accès refusé.</p>
        ) : (
          <ul className="mt-2 list-inside list-disc space-y-1 text-slate-300">
            {workflowGov.slice(0, 35).map((ev) => (
              <li key={ev.id}>
                <span className="font-mono text-xs">{ev.action}</span>
                {ev.label ? ` — ${ev.label}` : ""}
                {ev.actorName ? ` · ${ev.actorName}` : ""}{" "}
                <span className="text-slate-500">({new Date(ev.createdAt).toLocaleString("fr-FR")})</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="text-lg font-semibold text-white">Timeline passages</h2>
        <p className="mt-1 text-xs text-slate-500">
          Colonne « Exécution » = état terrain (démarrage agent). « Statut planning » = référence métier liste.
        </p>
        <div className="mt-3 overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-950 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Ordre</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Priorité</th>
                <th className="px-3 py-2">Heure prévue</th>
                <th className="px-3 py-2">Statut planning</th>
                <th className="px-3 py-2">Exécution passage</th>
                <th className="px-3 py-2">Validation</th>
                <th className="px-3 py-2">GPS</th>
                <th className="px-3 py-2">Photos</th>
                <th className="px-3 py-2">Facturable</th>
                <th className="px-3 py-2">Commentaire</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {timeline.map((it) => {
                const livePass = live.data?.tournee.passages?.find((p: LivePassage) => p.id === it.passageId);
                const qu = livePass?.qualityUi;
                const photoTxt =
                  qu != null
                    ? `${qu.photoPresentCount}/${qu.photoRequired}`
                    : livePass?.beforePhotoUrl || livePass?.afterPhotoUrl
                      ? "…"
                      : "—";
                return (
                  <tr key={it.passageId} className="bg-slate-900/30">
                    <td className="px-3 py-2">{it.ordre ?? "—"}</td>
                    <td className="px-3 py-2">{it.client}</td>
                    <td className="px-3 py-2">{it.type ?? "—"}</td>
                    <td className="px-3 py-2">
                      {it.priorite === "URGENT" || String(it.priorite).toUpperCase().includes("URGENT")
                        ? it.executionStatus === "REALISE"
                          ? "Urgence traitée"
                          : "Urgence (priorité)"
                        : it.priorite}
                    </td>
                    <td className="px-3 py-2">
                      {new Date(it.datePrevue).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-3 py-2">{labelStatutPlanning(it.statut)}</td>
                    <td className="px-3 py-2 font-medium text-emerald-200/95">{labelPassageExecution(it.executionStatus)}</td>
                    <td className="px-3 py-2 text-xs text-slate-300">{qu?.validationLabel ?? "—"}</td>
                    <td className="max-w-[140px] truncate px-3 py-2 text-xs text-slate-300" title={qu ? frGpsStatus(qu.gpsStatus, qu.gpsReason) : undefined}>
                      {qu ? frGpsStatus(qu.gpsStatus, qu.gpsReason) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-300">{photoTxt}</td>
                    <td className="px-3 py-2 text-xs text-slate-300">
                      {qu ? (qu.isFacturable ? "Oui" : "Non") : "—"}
                    </td>
                    <td className="px-3 py-2">{it.commentaire ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="text-lg font-semibold text-white">Actions</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {/* PLANIFIEE — Lancer */}
          {execStatus === "PLANIFIEE" ? (
            <button
              type="button"
              disabled={readOnly || mutations.pending.startTournee}
              onClick={() => void mutations.startTournee()}
              className="min-h-11 rounded-xl border border-emerald-500/55 bg-emerald-800/40 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-800/60 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {mutations.pending.startTournee ? "Lancement…" : "Lancer tournée"}
            </button>
          ) : null}

          {/* EN_COURS — rappel visuel : ne pas relancer */}
          {execStatus === "EN_COURS" ? (
            <button
              type="button"
              disabled
              title="Tournée déjà lancée"
              className="min-h-11 cursor-not-allowed rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-2 text-sm text-slate-500 line-through decoration-slate-600"
            >
              Lancer tournée
            </button>
          ) : null}

          {execStatus === "TERMINEE" ? (
            <span className="min-h-11 rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-500">
              Lancer tournée (terminée)
            </span>
          ) : null}

          {/* EN_COURS — Suspendre */}
          {execStatus === "EN_COURS" ? (
            <button
              type="button"
              disabled={readOnly || mutations.pending.pauseTournee}
              onClick={() => void mutations.pauseTournee()}
              className="min-h-11 rounded-xl border border-amber-500/55 bg-amber-950/35 px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-950/50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {mutations.pending.pauseTournee ? "Suspension…" : "Suspendre"}
            </button>
          ) : null}

          {/* SUSPENDUE — Reprendre */}
          {execStatus === "SUSPENDUE" ? (
            <button
              type="button"
              disabled={readOnly || mutations.pending.resumeTournee}
              onClick={() => void mutations.resumeTournee()}
              className="min-h-11 rounded-xl border border-emerald-500/55 bg-emerald-950/30 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-950/45 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {mutations.pending.resumeTournee ? "Reprise…" : "Reprendre"}
            </button>
          ) : null}

          {/* Suspendre masqué hors EN_COURS */}
          {execStatus !== "EN_COURS" && execStatus !== "SUSPENDUE" ? (
            <button
              type="button"
              disabled
              className="min-h-11 rounded-xl border border-slate-800 px-4 py-2 text-sm text-slate-600"
              title="Disponible uniquement pendant une tournée en cours"
            >
              Suspendre
            </button>
          ) : null}

          {/* Clôturer */}
          <button
            type="button"
            disabled={
              readOnly ||
              execStatus !== "EN_COURS" ||
              hasRunningPassage ||
              !closeGate ||
              mutations.pending.completeTournee
            }
            title={
              execStatus !== "EN_COURS"
                ? "Clôture possible uniquement quand la tournée est en cours"
                : hasRunningPassage
                  ? "Terminez ou annulez le passage en cours avant de clôturer"
                  : !closeGate
                    ? "Clôture impossible : passages restants, incident critique ou autre blocage métier"
                    : undefined
            }
            onClick={() => void onCloseTournee()}
            className="min-h-11 rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800/60 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {mutations.pending.completeTournee ? "Clôture…" : "Clôturer"}
          </button>

          <Link
            to={execHref}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-sky-500/45 px-4 py-2 text-sm font-semibold text-sky-200 hover:bg-sky-950/40"
          >
            Voir exécution terrain
            <ExternalLink className="h-4 w-4 opacity-80" aria-hidden />
          </Link>
        </div>
        {readOnly ? (
          <p className="mt-3 text-xs text-slate-500">Tournée terminée — actions de pilotage désactivées (lecture seule).</p>
        ) : null}
      </section>

      <ToastContainer theme="dark" position="top-right" />
    </div>
  );
}
